import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AllieChevronDown } from '../../../styling/icons';
// Imported directly (not via the top-level '../../../components' barrel):
// that barrel re-exports DataGrid -> DataGridToolbar -> DataGridDownload,
// which imports DownloadFormatMenu from this same ReportDownloader module —
// going through the barrel here would close that import cycle.
import { DarkTooltip } from '../../../components/DarkTooltip';
import {
  MenuWrapper,
  MenuTrigger,
  MenuChevron,
  MenuPanel,
  MenuItem,
  MenuErrorText,
  PANEL_WIDTH,
} from './DownloadFormatMenu.styles';

export type DownloadFormat = 'csv' | 'xlsx';

// English to match this package's convention for unoverridden fallback
// strings (e.g. the `downloadCsvTooltip || 'Download'` pattern elsewhere in
// this same file's siblings) -- ui-lib stays locale-neutral by default and
// lets each MFE override via props for its own locale.
const DEFAULT_ERROR_MESSAGE = 'Could not download the file. Please try again.';

export interface DownloadFormatMenuProps {
  /** Trigger icon (e.g. AllieDownloadIcon). */
  icon: React.ReactNode;
  /** Trigger label. Omit for an icon-only trigger (no chevron). */
  label?: string;
  /**
   * May return a Promise: while it's pending the trigger is disabled (blocks
   * double-clicks) and a rejection surfaces `errorMessage` instead of failing
   * silently.
   */
  onSelect: (format: DownloadFormat) => void | Promise<void>;
  csvLabel?: string;
  xlsxLabel?: string;
  className?: string;
  /** Accessible name for icon-only triggers (no visible label). */
  ariaLabel?: string;
  /** Disables the trigger and prevents opening the menu (e.g. while a download is in flight). */
  disabled?: boolean;
  /** Shown below the trigger when `onSelect` throws or its promise rejects. */
  errorMessage?: string;
}

const VIEWPORT_MARGIN = 16;
const DROPDOWN_GAP = 4;

/**
 * Button + dropdown to pick a download format (CSV / XLSX).
 * Same pattern as notifications/.../AssignDropdown.tsx: plain React +
 * styled-components + createPortal, no Ionic. Kept local to this package —
 * not meant to replace the other hand-rolled dropdowns elsewhere in the app.
 */
export const DownloadFormatMenu: React.FC<DownloadFormatMenuProps> = ({
  icon,
  label,
  onSelect,
  csvLabel = 'CSV',
  xlsxLabel = 'XLS',
  className,
  ariaLabel,
  disabled,
  errorMessage = DEFAULT_ERROR_MESSAGE,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((restoreFocus = false) => {
    setIsOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const availableBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN - DROPDOWN_GAP;
    const availableAbove = rect.top - VIEWPORT_MARGIN - DROPDOWN_GAP;
    const nextPlacement =
      availableBelow < 120 && availableAbove > availableBelow ? 'top' : 'bottom';

    setPlacement(nextPlacement);
    setPosition({
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN)
      ),
      top: nextPlacement === 'top' ? rect.top - DROPDOWN_GAP : rect.bottom + DROPDOWN_GAP,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePosition();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [close, isOpen, updatePosition]);

  const handleSelect = (format: DownloadFormat) => {
    close(true);
    setError(null);

    let result: void | Promise<void>;
    try {
      result = onSelect(format);
    } catch (err) {
      console.error('DownloadFormatMenu: onSelect failed', err);
      setError(errorMessage);
      return;
    }

    if (result && typeof (result as Promise<void>).then === 'function') {
      setIsBusy(true);
      (result as Promise<void>)
        .catch((err) => {
          console.error('DownloadFormatMenu: onSelect failed', err);
          setError(errorMessage);
        })
        .finally(() => setIsBusy(false));
    }
  };

  const isDisabled = disabled || isBusy;

  const handleTriggerClick = () => {
    if (isDisabled) return;
    setIsOpen((open) => !open);
    setError(null);
  };

  const trigger = (
    <MenuTrigger
      ref={triggerRef}
      type="button"
      disabled={isDisabled}
      $isOpen={isOpen}
      $iconOnly={!label}
      onClick={handleTriggerClick}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-controls={isOpen ? menuId : undefined}
      aria-label={!label ? ariaLabel : undefined}
    >
      {icon}
      {label}
      {label && (
        <MenuChevron $isOpen={isOpen}>
          <AllieChevronDown />
        </MenuChevron>
      )}
    </MenuTrigger>
  );

  return (
    <MenuWrapper ref={wrapperRef} className={className}>
      {!label && ariaLabel ? (
        <DarkTooltip text={ariaLabel}>{trigger}</DarkTooltip>
      ) : (
        trigger
      )}

      {typeof document !== 'undefined' &&
        isOpen &&
        ReactDOM.createPortal(
          <MenuPanel
            ref={panelRef}
            id={menuId}
            role="menu"
            $placement={placement}
            style={{ left: position.left, top: position.top }}
          >
            <MenuItem role="menuitem" type="button" onClick={() => handleSelect('csv')}>
              {csvLabel}
            </MenuItem>
            <MenuItem
              role="menuitem"
              type="button"
              $variant="xlsx"
              onClick={() => handleSelect('xlsx')}
            >
              {xlsxLabel}
            </MenuItem>
          </MenuPanel>,
          document.body
        )}

      {error && <MenuErrorText role="alert">{error}</MenuErrorText>}
    </MenuWrapper>
  );
};
